import type { components } from '@helmetcare/api-client';

export const BASE_URL: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: components['schemas']['ApiError'];
}

export interface ApiErrorDetail {
  code: string;
  message: string;
  details?: Array<{ field: string; reason: string }>;
  requestId: string;
  retryable: boolean;
}

export class ApiError extends Error {
  constructor(
    public code: string,
    public message: string,
    public details?: Array<{ field: string; reason: string }>,
    public requestId?: string,
    public retryable?: boolean,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let refreshTokenPromise: Promise<void> | null = null;

interface AuthStoreModule {
  default: {
    accessToken: { value: string | null };
    refreshToken: { value: string | null };
    setTokens: (accessToken: string, refreshToken: string) => void;
    clearAuth: () => void;
  };
}

// Lazy load auth store to avoid circular imports
async function getAuthStore(): Promise<AuthStoreModule['default']> {
  const module: AuthStoreModule = await import('../stores/auth');
  return module.default;
}

async function fetchWithAuth<T>(
  path: string,
  options: RequestInit & { skipAuth?: boolean; keepEnvelope?: boolean; retryAuth?: boolean } = {},
): Promise<T> {
  const { skipAuth, keepEnvelope, retryAuth = true, ...init } = options;

  const headers = new Headers(init.headers || {});
  headers.set('Content-Type', 'application/json');

  if (!skipAuth) {
    const auth = await getAuthStore();
    if (auth.accessToken.value) {
      headers.set('Authorization', `Bearer ${auth.accessToken.value}`);
    }
  }

  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;

  let response = await fetch(url, {
    ...init,
    headers,
  });

  // Handle 401: Try refresh, then retry
  if (response.status === 401 && !skipAuth && retryAuth) {
    try {
      await tryRefreshToken();
      const auth = await getAuthStore();
      if (auth.accessToken.value) {
        headers.set('Authorization', `Bearer ${auth.accessToken.value}`);
        response = await fetch(url, {
          ...init,
          headers,
        });
      }
    } catch {
      // Refresh failed, clear auth and redirect
      const auth = await getAuthStore();
      auth.clearAuth();
      window.location.href = '/login';
      throw new ApiError('AUTH_FAILED', '认证失败，请重新登录');
    }
  }

  const text = await response.text();
  let data: Record<string, unknown> = {};

  try {
    data = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  } catch {
    if (!response.ok) {
      throw new ApiError('INTERNAL_ERROR', `HTTP ${response.status}`);
    }
    return data as T;
  }

  if (!response.ok) {
    const error = data?.error as Record<string, unknown> | undefined;
    throw new ApiError(
      (error?.code as string) || `HTTP_${response.status}`,
      (error?.message as string) || `HTTP ${response.status}`,
      (error?.details as Array<{ field: string; reason: string }>) || undefined,
      (error?.requestId as string) || undefined,
      (error?.retryable as boolean) || undefined,
    );
  }

  // 分页调用要留住 meta；其余场景仍然拆信封，页面代码不必层层 .data
  if (keepEnvelope) {
    return data as T;
  }

  return (data?.data as T) ?? (data as T);
}

export async function tryRefreshToken(): Promise<void> {
  if (refreshTokenPromise) {
    await refreshTokenPromise;
    return;
  }

  const promise = (async () => {
    const auth = await getAuthStore();
    const rt = auth.refreshToken.value;
    if (!rt) {
      throw new ApiError('AUTH_FAILED', 'No refresh token');
    }

    try {
      const response = await fetchWithAuth<components['schemas']['AuthTokenResponse']>(
        '/auth/refresh',
        {
          method: 'POST',
          body: JSON.stringify({ refreshToken: rt }),
          skipAuth: true,
        },
      );

      auth.setTokens(response.accessToken, response.refreshToken);
    } finally {
      refreshTokenPromise = null;
    }
  })();

  refreshTokenPromise = promise;
  await promise;
}

export async function apiFetch<T>(
  path: string,
  options?: RequestInit & { skipAuth?: boolean; retryAuth?: boolean },
): Promise<T> {
  return fetchWithAuth<T>(path, options);
}

/** Binary downloads use the same refresh gate; bearer tokens never enter download URLs. */
export async function apiDownload(path: string): Promise<Blob> {
  const auth = await getAuthStore();
  const headers = new Headers();
  if (auth.accessToken.value) headers.set('Authorization', `Bearer ${auth.accessToken.value}`);
  const url = `${BASE_URL}${path}`;
  let response = await fetch(url, { headers });
  if (response.status === 401) {
    await tryRefreshToken();
    if (auth.accessToken.value) headers.set('Authorization', `Bearer ${auth.accessToken.value}`);
    response = await fetch(url, { headers });
  }
  if (!response.ok)
    throw new ApiError(`HTTP_${response.status}`, `日志下载失败（${response.status}）`);
  return response.blob();
}

/** 后端分页信封（OpenAPI PageMeta），列表数据在 data、总数在 meta */
interface PagedEnvelope<T> {
  data?: T[];
  meta?: { total?: number; page?: number; pageSize?: number; limit?: number };
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * 分页请求。
 *
 * 单独一条通路是因为 apiFetch 会把信封拆掉只返回 data，
 * 而分页的 total 在 meta 里——用 apiFetch 取列表会永远拿不到总数。
 */
export async function apiFetchPaged<T>(
  path: string,
  options?: RequestInit & { skipAuth?: boolean },
): Promise<Paged<T>> {
  const raw = await fetchWithAuth<PagedEnvelope<T> | T[]>(path, {
    ...options,
    keepEnvelope: true,
  });

  // 少数端点直接返回数组，没有 meta
  if (Array.isArray(raw)) {
    return { items: raw, total: raw.length, page: 1, pageSize: raw.length };
  }

  const items = raw.data ?? [];
  return {
    items,
    total: raw.meta?.total ?? items.length,
    page: raw.meta?.page ?? 1,
    pageSize: raw.meta?.pageSize ?? raw.meta?.limit ?? items.length,
  };
}

export default fetchWithAuth;
